import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Paper
} from '@mui/material';
import permissionService from '../services/permissionService';
import { PageTitleContainer, RichTextEditor } from '../components/common';

// Wrapper with page-level styling for sub-pages
const SubPageWrapper = ({ children }) => (
  <Box sx={{
    width: '100%',
    bgcolor: '#f5f6f7',
    minHeight: '100vh',
    pb: 4,
    px: 6,
    pt: 4,
    overflowX: 'hidden',
    backgroundImage: 'none',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  }}>
    {children}
  </Box>
);

const CreatePermissionView = ({ onCreated, editPermission = null }) => {
  const [name, setName] = useState(editPermission?.name || '');
  const [path, setPath] = useState(editPermission?.path || '');
  const [description, setDescription] = useState(editPermission?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const parentPath = path.includes('.') ? path.split('.').slice(0, -1).join('.') : '';

  const handleSave = async () => {
    if (!name.trim() || !path.trim()) {
      setError('Name and path are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editPermission) {
        await permissionService.updatePermission(editPermission.id || editPermission.path, { name, description });
      } else {
        await permissionService.createPermission({ name, path, description, permission_type: 'feature' });
      }
      setName(''); setPath(''); setDescription('');
      onCreated && onCreated();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to create permission');
    }
    setSaving(false);
  };
  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 230px)');
  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);
  return (
    <Box>
      <PageTitleContainer
        title={'Create permission'}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />
      <Paper sx={{
        borderRadius: '4px',
        p: 5,
        width: '100%',
        height: contentHeight,
        bgcolor: 'white',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        backgroundImage: 'none',
        '&::before': {
          display: 'none'
        },
        '&::after': {
          display: 'none'
        }
      }}>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ mt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Permission name <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField fullWidth size="small" value={name}
              onChange={e => setName(e.target.value)} placeholder="e.g. Edit Analysis"
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  color: 'var(--color-blue, #475969)',
                  height: '45px',
                  transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4759694d',
                  border: '1px solid #4759694d',
                  borderRadius: '4px',
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '14px',
                  color: 'var(--color-blue, #475969)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: '14px',
                }
              }} />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Path <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField fullWidth size="small" value={path}
              onChange={e => setPath(e.target.value)}
              placeholder="e.g. project.foundation.analysis.edit"
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  color: 'var(--color-blue, #475969)',
                  height: '45px',
                  transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4759694d',
                  border: '1px solid #4759694d',
                  borderRadius: '4px',
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '14px',
                  color: 'var(--color-blue, #475969)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: '14px',
                }
              }}
              helperText="Dot-separated path. Lowercase letters, numbers, underscores only." />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>Parent permission (auto-detected)</Typography>
            <TextField fullWidth size="small" value={parentPath} disabled
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  color: 'var(--color-blue, #475969)',
                  height: '45px',
                  transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4759694d',
                  border: '1px solid #4759694d',
                  borderRadius: '4px',
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '14px',
                  color: 'var(--color-blue, #475969)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: '14px',
                }
              }} />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>Description</Typography>
            <RichTextEditor
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does this permission control?"
              minHeight="150px"
              sx={{
                '& .ck-editor__editable': {
                  minHeight: '150px',
                  p: 2,
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  color: 'var(--color-blue, #475969)',
                  '& .ck-placeholder': {
                    color: '#a2abb3',
                    opacity: 0.7,
                    fontSize: '14px'
                  }
                },
                '& .ck-toolbar': {
                  border: 'none',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
          <Button variant="outlined" onClick={() => onCreated && onCreated()} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', bgcolor: '#333', '&:hover': { bgcolor: '#555' } }}
          >
            {saving ? <CircularProgress size={18} /> : editPermission ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Paper>

    </Box>
  );
};

const CreatePermissionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editPermission, setEditPermission] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const editId = searchParams.get('edit');

  useEffect(() => {
    if (editId) {
      setLoadingEdit(true);
      permissionService.getPermissionById(editId)
        .then(res => setEditPermission(res.data || res))
        .catch(err => console.error('Failed to load permission for editing:', err))
        .finally(() => setLoadingEdit(false));
    } else {
      setEditPermission(null);
    }
  }, [editId]);

  if (loadingEdit) {
    return <SubPageWrapper><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></SubPageWrapper>;
  }

  return <SubPageWrapper><CreatePermissionView editPermission={editPermission} onCreated={() => navigate('/permissions/manage')} /></SubPageWrapper>;
};

export default CreatePermissionPage;
export { CreatePermissionView };
